import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  BarChart3, TrendingUp, TrendingDown, Calendar, ArrowLeft, 
  DollarSign, Activity, Droplets, Zap, RefreshCw, ChevronDown, Users, X, Check
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton, SkeletonStats, SkeletonChart } from "@/components/ui/skeleton";
import axios from "axios";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend, ComposedChart, Line
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAdminHeader() {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Custom tooltip component for chart
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-xl p-4 shadow-2xl">
        <p className="text-cyan-400 font-semibold text-sm mb-2">{label}</p>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-6">
            <span className="text-slate-400 text-xs">Transaksi</span>
            <span className="text-white font-bold">{data.total_transactions}</span>
          </div>
          <div className="flex items-center justify-between gap-6">
            <span className="text-slate-400 text-xs">Revenue</span>
            <span className="text-emerald-400 font-bold">
              Rp {(data.total_revenue || 0).toLocaleString('id-ID')}
            </span>
          </div>
          <div className="flex items-center justify-between gap-6">
            <span className="text-slate-400 text-xs">Admin Fee</span>
            <span className="text-amber-400 font-medium">
              Rp {(data.total_admin_fee || 0).toLocaleString('id-ID')}
            </span>
          </div>
          <div className="flex items-center justify-between gap-6 pt-1 border-t border-slate-700">
            <span className="text-slate-400 text-xs">PDAM</span>
            <span className="text-cyan-400">{data.pdam_count || 0}</span>
          </div>
          <div className="flex items-center justify-between gap-6">
            <span className="text-slate-400 text-xs">Lainnya</span>
            <span className="text-purple-400">{data.other_count || 0}</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [period, setPeriod] = useState("week");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  
  // Multi-select petugas filter
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userDropdownRef = useRef(null);
  
  const isAdmin = !!localStorage.getItem("admin_token");

  // Fetch users list for filter
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API}/admin/users?status=active`, {
          headers: getAdminHeader()
        });
        setUsers(response.data || []);
      } catch (err) {
        console.error("Failed to fetch users:", err);
      }
    };
    if (isAdmin) fetchUsers();
  }, [isAdmin]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      toast.error("Akses ditolak");
      navigate("/admin/login");
      return;
    }
    fetchAnalytics();
  }, [isAdmin, navigate, period]);

  const fetchAnalytics = useCallback(async (customPeriod = null) => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      params.append("period", customPeriod || period);
      
      if ((customPeriod || period) === "custom" && customStartDate && customEndDate) {
        params.append("start_date", customStartDate);
        params.append("end_date", customEndDate);
      }
      
      // Add user filter if selected
      if (selectedUserIds.length > 0) {
        params.append("user_ids", selectedUserIds.join(","));
      }
      
      const response = await axios.get(`${API}/admin/analytics?${params.toString()}`, {
        headers: getAdminHeader()
      });
      
      setAnalyticsData(response.data);
    } catch (err) {
      // Do NOT auto-clear tokens or redirect on 401. Admin only logs out via "Keluar" button.
      if (err.response?.status === 401) {
        toast.error("Gagal memuat data analytics (unauthorized).");
      } else {
        toast.error("Gagal memuat data analytics");
      }
    } finally {
      setIsLoading(false);
    }
  }, [period, customStartDate, customEndDate, selectedUserIds, navigate]);

  // Refetch when user filter changes
  useEffect(() => {
    if (isAdmin) {
      fetchAnalytics();
    }
  }, [selectedUserIds]);

  const handlePeriodChange = (newPeriod) => {
    if (newPeriod === "custom") {
      setShowCustomDate(true);
    } else {
      setShowCustomDate(false);
      setPeriod(newPeriod);
    }
  };

  const handleCustomDateApply = () => {
    if (!customStartDate || !customEndDate) {
      toast.error("Pilih tanggal mulai dan akhir");
      return;
    }
    setPeriod("custom");
    fetchAnalytics("custom");
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000000) {
      return `Rp ${(amount / 1000000000).toFixed(1)}M`;
    } else if (amount >= 1000000) {
      return `Rp ${(amount / 1000000).toFixed(1)}Jt`;
    } else if (amount >= 1000) {
      return `Rp ${(amount / 1000).toFixed(0)}K`;
    }
    return `Rp ${amount.toLocaleString('id-ID')}`;
  };

  const formatYAxis = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(0)}Jt`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value;
  };

  const formatXAxis = (value) => {
    // Format based on interval
    if (!analyticsData) return value;
    
    if (analyticsData.interval === "hourly") {
      // Show only hour like "14:00"
      const parts = value.split(" ");
      return parts[1] || value;
    } else if (analyticsData.interval === "daily") {
      // Show like "17 Apr"
      try {
        const date = new Date(value);
        return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' });
      } catch {
        return value;
      }
    } else if (analyticsData.interval === "monthly") {
      // Show like "Apr 26"
      try {
        const [year, month] = value.split("-");
        const date = new Date(year, parseInt(month) - 1, 1);
        return date.toLocaleDateString('id-ID', { month: 'short', year: '2-digit' });
      } catch {
        return value;
      }
    }
    return value;
  };

  const periodButtons = [
    { id: "day", label: "24 Jam", icon: Activity },
    { id: "week", label: "1 Minggu", icon: Calendar },
    { id: "month", label: "1 Bulan", icon: Calendar },
    { id: "year", label: "1 Tahun", icon: Calendar },
    { id: "custom", label: "Pilih Tanggal", icon: Calendar },
  ];

  // Toggle user selection
  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Clear all user filters
  const clearUserFilter = () => {
    setSelectedUserIds([]);
  };

  // Get selected user names for display
  const getSelectedUserNames = () => {
    if (selectedUserIds.length === 0) return "Semua Petugas";
    if (selectedUserIds.length === 1) {
      const user = users.find(u => u.id === selectedUserIds[0]);
      return user?.username || user?.name || "1 Petugas";
    }
    return `${selectedUserIds.length} Petugas`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-slate-900/80 backdrop-blur-xl border-b border-slate-800">
        <nav className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-heading font-semibold text-white">Analytics Dashboard</span>
          </div>
          <Link to="/admin">
            <Button variant="ghost" size="sm" className="gap-2 text-slate-400 hover:text-white hover:bg-slate-800">
              <ArrowLeft className="w-4 h-4" />
              Kembali
            </Button>
          </Link>
        </nav>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Period Filter */}
          <Card className="mb-6 bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-slate-400 text-sm font-medium">Periode:</span>
                <div className="flex flex-wrap gap-2">
                  {periodButtons.map((btn) => (
                    <Button
                      key={btn.id}
                      variant={period === btn.id || (btn.id === "custom" && showCustomDate) ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePeriodChange(btn.id)}
                      className={`gap-2 transition-all ${
                        period === btn.id || (btn.id === "custom" && showCustomDate)
                          ? "bg-gradient-to-r from-cyan-600 to-emerald-600 border-0 text-white shadow-lg shadow-cyan-500/25"
                          : "bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white hover:border-cyan-500/50"
                      }`}
                      data-testid={`period-${btn.id}`}
                    >
                      <btn.icon className="w-4 h-4" />
                      {btn.label}
                    </Button>
                  ))}
                </div>
                
                {/* User/Petugas Multi-Select Filter */}
                <div className="relative" ref={userDropdownRef}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowUserDropdown(!showUserDropdown)}
                    className={`gap-2 transition-all ${
                      selectedUserIds.length > 0
                        ? "bg-purple-600/20 border-purple-500 text-purple-300 hover:bg-purple-600/30"
                        : "bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                    }`}
                    data-testid="user-filter-btn"
                  >
                    <Users className="w-4 h-4" />
                    {getSelectedUserNames()}
                    <ChevronDown className={`w-4 h-4 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} />
                  </Button>
                  
                  {/* Dropdown */}
                  <AnimatePresence>
                    {showUserDropdown && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full left-0 mt-2 w-64 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden"
                      >
                        <div className="p-2 border-b border-slate-700 flex items-center justify-between">
                          <span className="text-xs text-slate-400 font-medium">Pilih Petugas/Agen</span>
                          {selectedUserIds.length > 0 && (
                            <button
                              onClick={clearUserFilter}
                              className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                            >
                              <X className="w-3 h-3" />
                              Clear
                            </button>
                          )}
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                          {users.length === 0 ? (
                            <p className="p-3 text-sm text-slate-500 text-center">Tidak ada petugas</p>
                          ) : (
                            users.map((user) => (
                              <button
                                key={user.id}
                                onClick={() => toggleUserSelection(user.id)}
                                className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-700/50 transition-colors ${
                                  selectedUserIds.includes(user.id) ? 'bg-purple-600/20' : ''
                                }`}
                                data-testid={`user-option-${user.id}`}
                              >
                                <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                                  selectedUserIds.includes(user.id)
                                    ? 'bg-purple-500 border-purple-500'
                                    : 'border-slate-600'
                                }`}>
                                  {selectedUserIds.includes(user.id) && (
                                    <Check className="w-3 h-3 text-white" />
                                  )}
                                </div>
                                <div className="flex-1 text-left">
                                  <p className="text-sm text-white">{user.username || user.name}</p>
                                  <p className="text-xs text-slate-500 capitalize">{user.role}</p>
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                
                {/* Refresh Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchAnalytics()}
                  disabled={isLoading}
                  className="ml-auto gap-2 bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                  data-testid="refresh-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              
              {/* Selected Users Chips */}
              {selectedUserIds.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-800 flex flex-wrap gap-2">
                  <span className="text-xs text-slate-500">Filter aktif:</span>
                  {selectedUserIds.map(userId => {
                    const user = users.find(u => u.id === userId);
                    return (
                      <span 
                        key={userId}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-purple-600/20 text-purple-300 text-xs rounded-full"
                      >
                        {user?.username || user?.name || userId}
                        <button 
                          onClick={() => toggleUserSelection(userId)}
                          className="hover:text-white"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              
              {/* Custom Date Picker */}
              {showCustomDate && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="mt-4 pt-4 border-t border-slate-800"
                >
                  <div className="flex flex-wrap items-end gap-4">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Tanggal Mulai</label>
                      <Input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="w-44 bg-slate-800 border-slate-700 text-white"
                        data-testid="custom-start-date"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Tanggal Akhir</label>
                      <Input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="w-44 bg-slate-800 border-slate-700 text-white"
                        data-testid="custom-end-date"
                      />
                    </div>
                    <Button
                      onClick={handleCustomDateApply}
                      className="bg-gradient-to-r from-cyan-600 to-emerald-600 text-white"
                      data-testid="apply-custom-date"
                    >
                      Terapkan
                    </Button>
                  </div>
                </motion.div>
              )}
            </CardContent>
          </Card>

          {/* Summary Cards */}
          {analyticsData && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Total Transactions */}
              <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-20 h-20 bg-cyan-500/10 rounded-full blur-2xl" />
                <CardContent className="p-5 relative">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                      <Activity className="w-5 h-5 text-cyan-400" />
                    </div>
                    {analyticsData.trend && (
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                        analyticsData.trend.percentage >= 0 
                          ? "bg-emerald-500/20 text-emerald-400"
                          : "bg-red-500/20 text-red-400"
                      }`}>
                        {analyticsData.trend.percentage >= 0 ? (
                          <TrendingUp className="w-3 h-3" />
                        ) : (
                          <TrendingDown className="w-3 h-3" />
                        )}
                        {Math.abs(analyticsData.trend.percentage)}%
                      </div>
                    )}
                  </div>
                  <p className="text-slate-400 text-xs font-medium mb-1">Total Transaksi</p>
                  <p className="text-3xl font-heading font-bold text-white" data-testid="total-transactions">
                    {analyticsData.summary.total_transactions.toLocaleString('id-ID')}
                  </p>
                </CardContent>
              </Card>

              {/* Total Revenue */}
              <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-500/10 rounded-full blur-2xl" />
                <CardContent className="p-5 relative">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-emerald-400" />
                    </div>
                  </div>
                  <p className="text-slate-400 text-xs font-medium mb-1">Total Revenue</p>
                  <p className="text-2xl font-heading font-bold text-emerald-400" data-testid="total-revenue">
                    {formatCurrency(analyticsData.summary.total_revenue)}
                  </p>
                </CardContent>
              </Card>

              {/* Total Admin Fee */}
              <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-20 h-20 bg-amber-500/10 rounded-full blur-2xl" />
                <CardContent className="p-5 relative">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-amber-400" />
                    </div>
                  </div>
                  <p className="text-slate-400 text-xs font-medium mb-1">Total Admin Fee</p>
                  <p className="text-2xl font-heading font-bold text-amber-400" data-testid="total-admin-fee">
                    {formatCurrency(analyticsData.summary.total_admin_fee)}
                  </p>
                </CardContent>
              </Card>

              {/* PDAM Count */}
              <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-20 h-20 bg-purple-500/10 rounded-full blur-2xl" />
                <CardContent className="p-5 relative">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <Droplets className="w-5 h-5 text-purple-400" />
                    </div>
                  </div>
                  <p className="text-slate-400 text-xs font-medium mb-1">Transaksi PDAM</p>
                  <p className="text-3xl font-heading font-bold text-purple-400" data-testid="pdam-count">
                    {analyticsData.summary.total_pdam.toLocaleString('id-ID')}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Main Chart - Trading Style */}
          <Card className="mb-6 bg-slate-900/50 border-slate-800" data-testid="main-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading text-white flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-white" />
                </div>
                Grafik Transaksi & Revenue
                {analyticsData && (
                  <span className="text-xs text-slate-500 font-normal ml-2">
                    ({analyticsData.interval === "hourly" ? "Per Jam" : 
                      analyticsData.interval === "daily" ? "Per Hari" : "Per Bulan"})
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {isLoading ? (
                <div className="h-80 flex items-center justify-center">
                  <div className="flex items-center gap-3 text-slate-500">
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    <span>Memuat data...</span>
                  </div>
                </div>
              ) : analyticsData?.data?.length > 0 ? (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={analyticsData.data} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorAdmin" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={formatXAxis}
                        stroke="#64748b"
                        tick={{ fill: '#94a3b8', fontSize: 11 }}
                        axisLine={{ stroke: '#334155' }}
                      />
                      <YAxis 
                        yAxisId="left"
                        tickFormatter={formatYAxis}
                        stroke="#64748b"
                        tick={{ fill: '#94a3b8', fontSize: 11 }}
                        axisLine={{ stroke: '#334155' }}
                      />
                      <YAxis 
                        yAxisId="right" 
                        orientation="right"
                        stroke="#64748b"
                        tick={{ fill: '#94a3b8', fontSize: 11 }}
                        axisLine={{ stroke: '#334155' }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend 
                        wrapperStyle={{ paddingTop: '20px' }}
                        formatter={(value) => <span className="text-slate-400 text-xs">{value}</span>}
                      />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="total_revenue"
                        name="Revenue"
                        stroke="#10b981"
                        strokeWidth={2}
                        fill="url(#colorRevenue)"
                      />
                      <Bar
                        yAxisId="right"
                        dataKey="total_transactions"
                        name="Transaksi"
                        fill="#06b6d4"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={40}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="total_admin_fee"
                        name="Admin Fee"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        dot={{ fill: '#f59e0b', strokeWidth: 0, r: 3 }}
                        activeDot={{ r: 5, fill: '#f59e0b' }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-80 flex items-center justify-center">
                  <div className="text-center">
                    <BarChart3 className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                    <p className="text-slate-500">Tidak ada data untuk periode ini</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Secondary Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Transaction Breakdown */}
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading text-white flex items-center gap-2">
                  <Droplets className="w-4 h-4 text-cyan-400" />
                  Breakdown per Layanan
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {analyticsData?.data?.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analyticsData.data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={formatXAxis}
                          stroke="#64748b"
                          tick={{ fill: '#94a3b8', fontSize: 10 }}
                        />
                        <YAxis 
                          stroke="#64748b"
                          tick={{ fill: '#94a3b8', fontSize: 10 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend formatter={(value) => <span className="text-slate-400 text-xs">{value}</span>} />
                        <Bar dataKey="pdam_count" name="PDAM" stackId="a" fill="#06b6d4" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="other_count" name="Lainnya" stackId="a" fill="#a855f7" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center">
                    <p className="text-slate-500 text-sm">Tidak ada data</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Revenue Trend */}
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading text-white flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                  Trend Revenue
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {analyticsData?.data?.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={analyticsData.data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <defs>
                          <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.6}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={formatXAxis}
                          stroke="#64748b"
                          tick={{ fill: '#94a3b8', fontSize: 10 }}
                        />
                        <YAxis 
                          tickFormatter={formatYAxis}
                          stroke="#64748b"
                          tick={{ fill: '#94a3b8', fontSize: 10 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area 
                          type="monotone" 
                          dataKey="total_revenue" 
                          name="Revenue"
                          stroke="#10b981" 
                          strokeWidth={2}
                          fill="url(#colorAmount)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center">
                    <p className="text-slate-500 text-sm">Tidak ada data</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
